import { z } from 'zod';
import { getRequest, postRequest } from './amplify/helpers';
import {
  MediaAssetType,
  StandaloneMediaAsset,
  CompletedStandaloneMediaAsset,
  MediaAssetVideoProvider,
  TryOnProvider,
  MediaAssetImageProvider,
  StabilityAIAspectRatio,
  PlacementType,
} from '@/types/media';
import { UserUploadedFileSchema } from './upload-temp-file';
import { ChatSession } from '@/types/chat';
import {
  VideoMediaAssetDuration,
  VideoMediaAssetMode,
} from '@/features/ai-video-asset-creator/components/AIVideoAssetCreator';
import { TemplateName } from '@/features/studio-video-templates/types/template';
import { ModelSettings } from '@/features/ai-try-on/constants/modelGenerationDefaults';
const urlPrefix = '/media-asset';

const DeleteMediaAssetByIdSchema = z.object({
  appKey: z.string(),
  id: z.string(),
  type: z.nativeEnum(MediaAssetType),
});

export const deleteMediaAssetById = async (
  body: z.infer<typeof DeleteMediaAssetByIdSchema>
): Promise<{ mediaAsset: StandaloneMediaAsset }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/delete`,
    options: { body },
  });

const UpdateMediaAssetByIdSchema = z.object({
  appKey: z.string(),
  id: z.string(),
  type: z.nativeEnum(MediaAssetType),
  name: z.string(),
});

export const updateMediaAssetById = async (
  body: z.infer<typeof UpdateMediaAssetByIdSchema>
): Promise<{ mediaAsset: StandaloneMediaAsset }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/update`,
    options: { body },
  });

const GetMediaAssetByIdSchema = z.object({
  appKey: z.string(),
  id: z.string(),
  type: z.nativeEnum(MediaAssetType),
});

export const getMediaAssetById = async (
  body: z.infer<typeof GetMediaAssetByIdSchema>
): Promise<{ mediaAsset: StandaloneMediaAsset }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/by-id`,
    options: { body },
  });

const GetMediaAssetByIdsSchema = z.object({
  mediaAssetIds: z
    .array(
      z.object({
        appKey: z.string(),
        id: z.string(),
        type: z.nativeEnum(MediaAssetType),
      })
    )
    .min(1),
});
export const getMediaAssetByIds = async (
  body: z.infer<typeof GetMediaAssetByIdsSchema>
): Promise<{ mediaAssets: StandaloneMediaAsset[] }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/by-ids`,
    options: { body },
  });

const GenerateImageSchema = z.object({
  appKey: z.string(),
  appUrl: z.string(),
  imagePrompt: z.string().min(1),
  productId: z.string().optional(),
  productTitle: z.string().optional(),
  autogenerated: z.boolean(),
  aspectRatio: z.nativeEnum(StabilityAIAspectRatio).optional(),
});
export const generateImage = async (body: z.infer<typeof GenerateImageSchema>): Promise<StandaloneMediaAsset> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-image`,
    options: { body },
  });

const ReplaceBackgroundSchema = z
  .object({
    appKey: z.string(),
    appUrl: z.string(),
    image: z.string().min(1),
    backgroundPrompt: z.string().optional(),
    productId: z.string().optional(),
    productTitle: z.string().optional(),
    autogenerated: z.boolean(),
    aspectRatio: z.enum(['9:16', '16:9', '1:1']).optional(),
    backgroundReferenceImage: z.string().optional(),
    provider: z.nativeEnum(MediaAssetImageProvider).optional(),
    placementType: z.nativeEnum(PlacementType).optional(),
  })
  .refine((data) => !!data.backgroundPrompt?.trim() || !!data.backgroundReferenceImage, {
    message: 'Either backgroundPrompt or backgroundReferenceImage must be provided',
    path: ['backgroundPrompt'],
  });

export interface ReplaceBackgroundResponse {
  mediaAssets: StandaloneMediaAsset[];
}

export const replaceBackground = async (
  body: z.infer<typeof ReplaceBackgroundSchema>
): Promise<ReplaceBackgroundResponse> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/replace-image-background`,
    options: { body },
  });

const GenerateChatToImageSchema = z.object({
  appKey: z.string(),
  appUrl: z.string(),
  imagePrompt: z.string().min(1),
  images: z.array(z.string()).optional(),
  productId: z.string().optional(),
  productTitle: z.string().optional(),
  autogenerated: z.boolean(),
  startChat: z.boolean().optional(),
  selectedChatSession: z.string().optional(),
});
export const generateChatToImage = async (
  body: z.infer<typeof GenerateChatToImageSchema>
): Promise<StandaloneMediaAsset | string> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-chat-to-image`,
    options: { body },
  });

const GenerateTryOnAvatarSchema = z.object({
  appKey: z.string(),
  appUrl: z.string(),
  modelSettings: z.record(z.string(), z.string()),
});
export const generateTryOnAvatar = async (
  body: z.infer<typeof GenerateTryOnAvatarSchema>
): Promise<StandaloneMediaAsset> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-try-on-avatar`,
    options: { body },
  });

const GenerateTryOnAvatarBundleSchema = z.object({
  appKey: z.string(),
  appUrl: z.string(),
  modelSettings: z.record(z.string(), z.string()),
});
export const generateTryOnAvatarBundle = async (
  body: z.infer<typeof GenerateTryOnAvatarBundleSchema>
): Promise<StandaloneMediaAsset[]> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-try-on-avatar/bundle`,
    options: { body },
  });

export const getCustomTryOnAvatarSettingsDefinition = async (): Promise<{
  settingsDefinition: Record<string, { name: string; feature: string; options: string[] }>;
}> =>
  getRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-try-on-avatar/settings-definition`,
  });

export const extractModelSettingsFromImage = async (imageUrl: string): Promise<{ settings: ModelSettings }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-try-on-avatar/extract-settings`,
    options: { body: { imageUrl } },
  });

export const getDefaultTryOnModels = async (): Promise<{ models: StandaloneMediaAsset[] }> => {
  /** we cache the responve so just change the ver to reset browser cache for users if you want to reflect the changes immediately */
  const version = '1';
  return getRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-try-on-avatar/default-models`,
    options: { queryParams: { version } },
  });
};

const GeneratePersonalAvatarImageSchema = z.object({
  appKey: z.string(),
  imagePrompt: z.string().min(1),
  imageName: z.string().optional(),
});
export const generatePersonalAvatarImage = async (
  body: z.infer<typeof GeneratePersonalAvatarImageSchema>
): Promise<StandaloneMediaAsset> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-personal-avatar-image`,
    options: { body },
  });

const GenerateVideoSchema = z.object({
  image: z.string().min(1),
  videoPrompt: z.string().min(1).optional(),
  backgroundPrompt: z.string().optional(),
  appKey: z.string(),
  appUrl: z.string(),
  productId: z.string().optional(),
  productTitle: z.string().optional(),
  autogenerated: z.boolean(),
  isSuggested: z.boolean().optional(),
  provider: z.nativeEnum(MediaAssetVideoProvider).optional(),
  duration: z.nativeEnum(VideoMediaAssetDuration).optional(),
  mode: z.nativeEnum(VideoMediaAssetMode).optional(),
});

export type GenerateVideoRequest = z.infer<typeof GenerateVideoSchema>;
export const generateVideo = async (body: GenerateVideoRequest): Promise<StandaloneMediaAsset> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-video`,
    options: { body },
  });

const SelectProductImageToAnimateSchema = z.object({
  appKey: z.string(),
  appUrl: z.string(),
  productIds: z.array(z.string()).min(1),
});
export type SelectProductImageToAnimateRequest = z.infer<typeof SelectProductImageToAnimateSchema>;
export const selectProductImageToAnimate = async (
  body: SelectProductImageToAnimateRequest
): Promise<{ images: Record<string, string | null> }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-video/select-product-image`,
    options: { body },
  });

const SelectFrontBackProductImageSchema = z.object({
  appKey: z.string(),
  appUrl: z.string(),
  productIds: z.array(z.string()).min(1),
});
export type SelectFrontBackProductImageRequest = z.infer<typeof SelectFrontBackProductImageSchema>;
export const selectFrontBackProductImage = async (
  body: SelectFrontBackProductImageRequest
): Promise<{ images: Record<string, { front: string; back: string; type?: 'garment' | 'accessory' | 'shoes' }> }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-video/select-front-back-product-images`,
    options: { body },
  });

const GetAiImagesSchema = z.object({
  appKey: z.string(),
});
export const getAiImages = async (
  body: z.infer<typeof GetAiImagesSchema>
): Promise<{ mediaAssets: StandaloneMediaAsset[] }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/get-ai-images`,
    options: { body },
  });

const GetAiChatSessionsSchema = z.object({
  appKey: z.string(),
});
export const getAiChatSessions = async (body: z.infer<typeof GetAiChatSessionsSchema>): Promise<ChatSession[]> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/get-ai-chat-sessions`,
    options: { body },
  }).then((res) => res.chatSessions);

const GetAiVideosSchema = z.object({
  appKey: z.string(),
});
export const getAiVideos = async (
  body: z.infer<typeof GetAiVideosSchema>
): Promise<{ mediaAssets: StandaloneMediaAsset[] }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/get-ai-videos`,
    options: { body },
  });

const GetUploadedMediaAssetsSchema = z.object({
  appKey: z.string(),
});
export const getUploadedMediaAssets = async (
  body: z.infer<typeof GetUploadedMediaAssetsSchema>
): Promise<{ mediaAssets: StandaloneMediaAsset[] }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/get-uploaded`,
    options: { body },
  });

const GenerateTextToVideoSchema = z.object({
  videoPrompt: z.string().min(1),
  appKey: z.string(),
  productId: z.string().optional(),
  productTitle: z.string().optional(),
});
export const generateTextToVideo = async (
  body: z.infer<typeof GenerateTextToVideoSchema>
): Promise<StandaloneMediaAsset> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/text-to-video`,
    options: { body },
  });

const GenerateAudioSchema = z.object({
  prompt: z.string(),
  instrumental: z.boolean(),
  tags: z.array(z.string()).optional(),
  appKey: z.string(),
  autogenerated: z.boolean(),
  productId: z.string().optional(),
});
export const generateAudio = async (
  body: z.infer<typeof GenerateAudioSchema>
): Promise<{ mediaAssets: StandaloneMediaAsset[] }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-audio`,
    options: { body },
  });

const GenerateTryOnSchema = z.object({
  appKey: z.string(),
  appUrl: z.string(),
  productId: z.string().optional(),
  avatarImageSrc: z.string().min(1),
  productImageSrc: z.string().min(1),
  tryOnImageTitle: z.string().optional(),
  provider: z.nativeEnum(TryOnProvider).optional(),
});
export const generateTryOn = async (body: z.infer<typeof GenerateTryOnSchema>): Promise<StandaloneMediaAsset> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-try-on-image`,
    options: { body },
  });

const UploadMediaAssetSchema = z.object({
  appKey: z.string(),
  appUrl: z.string(),
  file: UserUploadedFileSchema,
});
export type UploadMediaAssetRequest = z.infer<typeof UploadMediaAssetSchema>;

export const uploadMediaAsset = async (
  body: z.infer<typeof UploadMediaAssetSchema>
): Promise<{ mediaAsset: CompletedStandaloneMediaAsset }> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/upload`,
    options: { body },
  });

const CreateProductsThemeSchema = z.object({
  appKey: z.string(),
  appUrl: z.string(),
  productIds: z.array(z.string()).min(1),
  collectionIds: z.array(z.string()).optional(),
  prompt: z.string().optional(),
});

export type CreateProductsThemeRequest = z.infer<typeof CreateProductsThemeSchema>;

export type ThemeSuggestion = {
  theme: string;
  title?: string;
  subtitle?: string;
  backgroundPrompt: string;
  backgroundImage?: {
    imageUrl: string;
  };
};

export const createProductsTheme = async (
  body: CreateProductsThemeRequest
): Promise<{
  suggestions: Array<ThemeSuggestion>;
}> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-video/create-products-themes`,
    options: { body },
  });

export const LastMediaAssetSchema = z.object({
  appKey: z.string(),
  productId: z.string().optional(),
});
export type LastMediaAssetRequest = z.infer<typeof LastMediaAssetSchema>;

export type LastMediaAssetResponse = {
  mediaAsset: CompletedStandaloneMediaAsset | null;
};

export const lastMediaAsset = async (request: LastMediaAssetRequest): Promise<LastMediaAssetResponse> => {
  return await postRequest({
    apiName: 'studio',
    path: '/media-asset/get-last-creation',
    options: {
      body: request,
    },
  });
};

const CreateUploadedTryOnAvatarSchema = z.object({
  appKey: z.string(),
  appUrl: z.string(),
  src: z.string(),
  fileExtension: z.string(),
  modelName: z.string().optional(),
  addToDefaultModels: z.boolean().optional(),
});
export type CreateUploadedTryOnAvatarRequest = z.infer<typeof CreateUploadedTryOnAvatarSchema>;
export const createUploadedTryOnAvatar = async (
  body: CreateUploadedTryOnAvatarRequest
): Promise<CompletedStandaloneMediaAsset> =>
  postRequest({
    apiName: 'studio',
    path: `${urlPrefix}/generate-video/create-uploaded-try-on-avatar`,
    options: { body },
  });
